import React, { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export type TestimonialScreenshot = {
  id: string;
  src: string;
  alt: string;
};

type TestimonialScreensCarouselProps = {
  items: TestimonialScreenshot[];
  autoMs?: number;
  className?: string;
};

const AUTO_MS_DEFAULT = 6500;

function wrapOffset(index: number, active: number, total: number): number {
  let diff = index - active;
  const half = total / 2;
  if (diff > half) diff -= total;
  if (diff < -half) diff += total;
  return diff;
}

const TestimonialScreensCarousel: React.FC<TestimonialScreensCarouselProps> = ({
  items,
  autoMs = AUTO_MS_DEFAULT,
  className = "",
}) => {
  const total = items.length;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback(
    (next: number) => {
      setIndex(((next % total) + total) % total);
    },
    [total]
  );

  useEffect(() => {
    if (paused || total <= 1) return;
    const timer = window.setInterval(() => go(index + 1), autoMs);
    return () => window.clearInterval(timer);
  }, [autoMs, go, index, paused, total]);

  if (total === 0) return null;

  return (
    <div
      className={`testimonial-screens-carousel ${className}`.trim()}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-roledescription="carrossel"
      aria-label="Depoimentos em prints"
    >
      <div className="testimonial-screens-carousel__stage">
        {items.map((item, i) => {
          const offset = wrapOffset(i, index, total);
          if (Math.abs(offset) > 1) return null;

          const positionClass =
            offset === 0 ? "is-center" : offset < 0 ? "is-left" : "is-right";

          return (
            <button
              key={item.id}
              type="button"
              className={`testimonial-screens-carousel__slide ${positionClass}`}
              onClick={() => {
                if (offset !== 0) go(i);
              }}
              aria-label={
                offset === 0
                  ? `Depoimento em destaque: ${item.alt}`
                  : `Ver depoimento: ${item.alt}`
              }
              aria-current={offset === 0 ? "true" : undefined}
              tabIndex={offset === 0 ? 0 : -1}
            >
              <img src={item.src} alt={item.alt} loading="lazy" decoding="async" draggable={false} />
            </button>
          );
        })}
      </div>

      {total > 1 && (
        <>
          <button
            type="button"
            className="testimonial-screens-carousel__nav testimonial-screens-carousel__nav--prev"
            onClick={() => go(index - 1)}
            aria-label="Depoimento anterior"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden />
          </button>
          <button
            type="button"
            className="testimonial-screens-carousel__nav testimonial-screens-carousel__nav--next"
            onClick={() => go(index + 1)}
            aria-label="Próximo depoimento"
          >
            <ChevronRight className="h-5 w-5" aria-hidden />
          </button>

          <div className="testimonial-screens-carousel__dots">
            {items.map((item, i) => (
              <button
                key={item.id}
                type="button"
                className={`testimonial-screens-carousel__dot ${i === index ? "is-active" : ""}`}
                onClick={() => go(i)}
                aria-label={`Ir para depoimento ${i + 1}`}
                aria-current={i === index ? "true" : undefined}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default TestimonialScreensCarousel;
